import { firstValueFrom, Observable, Subject, Subscription, tap } from "rxjs";
import { BaseSelectStatementId } from "src/app/enums/base-select-statement-id.enum";
import { SelectStatementOptions } from "src/app/models/select-statement-options.model";
import { SelectStatementResult } from "src/app/models/select-statement-result.model";
import { TableDataOperator } from "src/app/operators/table-data.operator";
import { RequestOptions } from "src/app/services/base.service";
import { ServiceLocator } from "src/app/tools/service-locator.tool";
import { OperationType } from "../../filters/miscellaneous/operation-type.enum";
import { QueryFilterParameter } from "../../filters/models/query-filter-parameter.model";
import { BookmarkElement } from "../models/bookmark-element.model";
import { Bookmark } from "../models/bookmark.model";
import { BookmarksGroup } from "../models/bookmarks-group.model";
import { Bookmarks, BookmarkService } from "../services/bookmark.service";
import { StatusMessageResultDto } from "src/app/pages/general/project.service";
import { BookmarkElementsOperator } from "./elements-operators/bookmark-elements.operator";
import { MinifiedArgument } from "src/app/core/models/minified-argument.model";
import { NotificationService } from "src/app/services/notification.service";
import { isFunction } from "src/app/helpers/type-utils";
import { LocalStorageService } from "src/app/services/local-storage.service";


export abstract class BookmarksGroupOperator {

	/**
	 * Loads `BookmarksGroup` based on a logic implemented by a specific operator.
	 * Loading logic depends on developers' choice and data.
	 * 2 examples but could be any number of examples =
	 * 	1.A table in DB can contain relations between BookmarksGroup and some (key or id).
	 * 	2.You know the exact id of `BookmarksGroup` and just load it by id from DB.
	 * @param requestOptions Additional settings for convenience. Does not modify response.
	 */
	public abstract loadBookmarksGroup(requestOptions?: RequestOptions): Observable<BookmarksGroup>;

	private _bookmarkService: BookmarkService = ServiceLocator.injector.get(BookmarkService);

	// Don't use this property to access the value, it might be null or undefined. Use `bookmarksGroup`.
	private _bookmarksGroup: BookmarksGroup;

	// This one is a promise because we want to initialize it if it's undefined or null.
	// To avoid implementing loading logic in each inherited class and to ensure that this property is initialized.
	public get bookmarksGroup(): Promise<BookmarksGroup> {

		return new Promise<BookmarksGroup>(
			(resolve, reject) => {
				const localStorageKey = `${Bookmarks.LocalStorageKeys.BookmarksGroupPrefix}-route(${this.routeId})_operator(${this.id})`;

				this._bookmarksGroup = this._bookmarksGroup ?? this._localStorageService.get<BookmarksGroup>(localStorageKey);

				if (this._bookmarksGroup && this._bookmarksGroup.bookmarksGroupId) {
					resolve(this._bookmarksGroup);

					return;
				}

				this.loadBookmarksGroup(
					{
						name: 'Load Bookmarks Group',
						showLoadingIndicator: true,
						responseLoggingEnabled: false,
					}
				).subscribe(
					{
						next: (bookmarksGroup: BookmarksGroup) => {
							this._bookmarksGroup = bookmarksGroup;

							this._localStorageService.set<BookmarksGroup>(localStorageKey, this._bookmarksGroup);

							resolve(this._bookmarksGroup);
						},
						error: (error) => {
							reject(error);
						}
					}
				);
			}
		);
	}

	protected bookmarkElementsTableDataOperator: TableDataOperator<BookmarkElement> = new TableDataOperator<BookmarkElement>(
		new SelectStatementOptions(
			{
				baseSelectStatementId: BaseSelectStatementId.DEV_GetBookmarkElements,
			}
		),
		BookmarkElement
	);

	private _bookmarks: Bookmark[] = [];
	public get bookmarks(): Bookmark[] {
		return this._bookmarks;
	}

	protected bookmarksTableDataOperator: TableDataOperator<Bookmark> = new TableDataOperator<Bookmark>(
		new SelectStatementOptions(
			{
				filters: async () => [ // It's a function because `bookmarksGroup` could change and we would like to cover this situation.
					new QueryFilterParameter(
						'BookmarksGroupId',
						(await this.bookmarksGroup).bookmarksGroupId.toString(),
						OperationType.Equals
					)
				],
				baseSelectStatementId: BaseSelectStatementId.DEV_GetBookmarks,
			}
		),
		Bookmark,
		{
			getObservable: async (selectStatementOptions: SelectStatementOptions, requestOptions: RequestOptions) => {
				const observable$ = await this.bookmarksTableDataOperator.defaultGetObservable(selectStatementOptions, requestOptions);

				return observable$.pipe(
					tap(
						async (response: SelectStatementResult<Bookmark>) => {



							this._bookmarks = response.data;
						}
					)
				);
			},
			load: (selectStatementOptions: SelectStatementOptions, requestOptions: RequestOptions) => this.bookmarksTableDataOperator.defaultLoad(selectStatementOptions, requestOptions),
			localStorageKey: `${Bookmarks.LocalStorageKeys.BookmarksPrefix}-route(${this.routeId})_operator(${this.id})`
		}
	);

	private _bookmarkElementsOperators: BookmarkElementsOperator[] | (() => BookmarkElementsOperator[]);
	public get bookmarkElementsOperators(): BookmarkElementsOperator[] {
		const bookmarkElementsOperators: BookmarkElementsOperator[] = isFunction(this._bookmarkElementsOperators) ? this._bookmarkElementsOperators() : this._bookmarkElementsOperators;

		return bookmarkElementsOperators;
	}
	public set bookmarkElementsOperators(value: BookmarkElementsOperator[] | (() => BookmarkElementsOperator[])) {
		this._bookmarkElementsOperators = value;
	}

	public onBookmarkApplied: Subject<Bookmark> = new Subject<Bookmark>();

	public async applyBookmark(bookmark: Bookmark): Promise<boolean> {

		const applyBookmarkElements = (selectStatementResult: SelectStatementResult<BookmarkElement>) => {
			// Apply boookmark elements through operators to actual elements.
			// Also prefilter elements that belong to that operator.
			// But be aware that `null` `operatorId` elements would probably be filtered together unless you specify them even though they might not belong to the same operator. (This is usually the case for `DynamicBookmarkElementsOperator` because we would put unique not component specific elements logic in it.)
			for (let a = 0; a < this.bookmarkElementsOperators.length; a++) {
				this.bookmarkElementsOperators[a].applyBookmarkElements(
					selectStatementResult.data.filter(
						(value) => value.operatorId === this.bookmarkElementsOperators[a].id
					)
				);
			}

			for (let a = 0; a < this.bookmarkElementsOperators.length; a++) {
				this.bookmarkElementsOperators[a].onAllBookmarkElementsApplied?.();
			}

			this.onBookmarkApplied.next(bookmark);
		}

		if (bookmark.isDefault) {
			const bookmarkElementsSSR: SelectStatementResult<BookmarkElement> = this._localStorageService.get(`${Bookmarks.LocalStorageKeys.BookmarkElementsPrefix}-route(${this.routeId})_operator(${this.id})`);

			if (bookmarkElementsSSR && bookmarkElementsSSR.data.length > 0 && bookmarkElementsSSR.data[0].bookmarkId === bookmark.bookmarkId) {
				applyBookmarkElements(bookmarkElementsSSR)

				return true;
			}
		}

		// Load all the bookmark elements belonging to `bookmark`.
		(await this.bookmarkElementsTableDataOperator.getObservable(
			{
				name: 'Load bookmark elements for a bookmark on applyBookmark.',
				showLoadingIndicator: false,
				responseLoggingEnabled: false,
			},
			new SelectStatementOptions(
				{
					minifiedArguments: [
						new MinifiedArgument(
							'@BookmarkId',
							'bigint',
							bookmark.bookmarkId.toString()
						)
					],
					baseSelectStatementId: BaseSelectStatementId.DEV_GetBookmarkElements,
				}
			)
		)).subscribe(
			{
				next: (selectStatementResult: SelectStatementResult<BookmarkElement>) => {
					applyBookmarkElements(selectStatementResult);

					if (bookmark.isDefault) {
						this._localStorageService.set(`${Bookmarks.LocalStorageKeys.BookmarkElementsPrefix}-route(${this.routeId})_operator(${this.id})`, selectStatementResult);
					}
				},
				error: (err: any) => {
					return false;
				}
			}
		);

		return true;
	}

	public async createBookmark(name: string, isDefault: boolean): Promise<Bookmark> {
		const bookmark: Bookmark = new Bookmark(
			undefined,
			name,
			isDefault,
			(await this.bookmarksGroup).bookmarksGroupId
		);

		const bookmarkElements: BookmarkElement[] = [];

		for (let a = 0; a < this.bookmarkElementsOperators.length; a++) {
			bookmarkElements.push(...this.bookmarkElementsOperators[a].prepareBookmarkElements(bookmark));
		}

		if (bookmarkElements.length === 0) {
			return new Promise(
				(resolve, reject) => {
					this._notificationService.error('No elements for bookmark were selected.');

					reject('No elements for bookmark were selected.');
				}
			);
		}

		const observable$ = this._bookmarkService.createBookmark(
			{
				bookmark: bookmark,
				bookmarkElements: bookmarkElements,
			},
			{
				name: 'createBookmark',
				showLoadingIndicator: true,
				responseLoggingEnabled: false,
			}
		).pipe(
			tap(
				(value: Bookmark) => {
					if (value.isDefault) {
						for (let a = 0; a < this._bookmarks.length; a++) {
							this._bookmarks[a].isDefault = false;
						}

						// Invalidate default bookmark elements cache for this route.
						this._localStorageService.remove(`${Bookmarks.LocalStorageKeys.BookmarkElementsPrefix}-route(${this.routeId})_operator(${this.id})`);
					}

					this._bookmarks.push(value);

					// Invalidate bookmarks cache for this route.
					this._localStorageService.remove(`${Bookmarks.LocalStorageKeys.BookmarksPrefix}-route(${this.routeId})_operator(${this.id})`);
				}
			)
		);

		return firstValueFrom(
			observable$
		);
	}

	public async updateBookmark(bookmark: Bookmark): Promise<StatusMessageResultDto> {
		const observable$ = this._bookmarkService.updateBookmark(
			bookmark,
			{
				name: 'updateBookmark',
				showLoadingIndicator: true,
				responseLoggingEnabled: false,
			}
		).pipe(
			tap(
				(value: StatusMessageResultDto) => {
					// Invalidate bookmarks cache for this route.
					this._localStorageService.remove(`${Bookmarks.LocalStorageKeys.BookmarksPrefix}-route(${this.routeId})_operator(${this.id})`);
				}
			)
		);

		return firstValueFrom(
			observable$
		);
	}

	public async deleteBookmark(bookmark: Bookmark): Promise<StatusMessageResultDto> {
		const observable$ = this._bookmarkService.deleteBookmark(
			bookmark,
			{
				name: 'deleteBookmark',
				showLoadingIndicator: true,
				responseLoggingEnabled: false,
			}
		).pipe(
			tap(
				(value: StatusMessageResultDto) => {
					// Invalidate bookmarks cache for this route.
					this._localStorageService.remove(`${Bookmarks.LocalStorageKeys.BookmarksPrefix}-route(${this.routeId})_operator(${this.id})`);
				}
			)
		);

		return firstValueFrom(
			observable$
		);
	}

	public async updateBookmarkElements(bookmark: Bookmark): Promise<StatusMessageResultDto> {
		const bookmarkElements: BookmarkElement[] = [];

		for (let a = 0; a < this.bookmarkElementsOperators.length; a++) {
			bookmarkElements.push(...this.bookmarkElementsOperators[a].prepareBookmarkElements(bookmark));
		}

		if (bookmarkElements.length === 0) {
			return new Promise(
				(resolve, reject) => {
					this._notificationService.error('No elements for bookmark were selected.');

					reject('No elements for bookmark were selected.');
				}
			);
		}

		const observable$ = this._bookmarkService.updateBookmarkElements(
			{
				bookmark: bookmark,
				bookmarkElements: bookmarkElements,
			},
			{
				name: 'updateBookmarkElements',
				showLoadingIndicator: true,
				responseLoggingEnabled: false,
			}
		).pipe(
			tap(
				(value: StatusMessageResultDto) => {
					if (bookmark.isDefault) {
						// Invalidate default bookmark elements cache for this route.
						this._localStorageService.remove(`${Bookmarks.LocalStorageKeys.BookmarkElementsPrefix}-route(${this.routeId})_operator(${this.id})`);
					}
				}
			)
		);

		return firstValueFrom(
			observable$
		);
	}

	/**
	 * Gets an Observable that loads all the necessary data like `Bookmarks` and `BookmarkElements` of a specific `BookmarksGroup`.
	 */
	public async getObservable(requestOptions?: RequestOptions): Promise<Observable<SelectStatementResult<Bookmark>>> {

		return (await this.bookmarksTableDataOperator.getObservable(requestOptions)).pipe();
	}

	/**
	 * Subscribes to `getObservable` return value.
	 */
	public load: (requestOptions?: RequestOptions) => Promise<Subscription> = async (requestOptions?: RequestOptions) => (await this.getObservable(requestOptions)).subscribe();

	protected bookmarkService: BookmarkService = ServiceLocator.injector.get(BookmarkService);

	private _notificationService: NotificationService = ServiceLocator.injector.get(NotificationService);
	private _localStorageService: LocalStorageService = ServiceLocator.injector.get(LocalStorageService);

	/**
	 *
	 */
	constructor(
		protected id: number | string,
		protected routeId: number,
		bookmarkElementsOperators: BookmarkElementsOperator[] | (() => BookmarkElementsOperator[]),
	) {
		this._bookmarkElementsOperators = bookmarkElementsOperators;
	}
}
